import React, { useEffect, useState } from 'react';
import './leadCaptureAnalytics.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RANGES = [7, 30, 90];

function fmtNum(n) { return new Intl.NumberFormat('en-US').format(n || 0); }
function fmtRelative(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function Sparkline({ series }) {
  if (!series?.length) return null;
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <div className="lca-spark" aria-hidden="true">
      {series.map((s, i) => (
        <span key={i} style={{ height: `${Math.max(2, (s.views / max) * 28)}px` }} title={`${s.date}: ${s.views} views`} />
      ))}
    </div>
  );
}

export default function MemberLeadCaptureAnalytics() {
  const [range, setRange] = useState(7);
  const [summary, setSummary] = useState(null);
  const [seriesByPage, setSeriesByPage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPage, setExpandedPage] = useState(null);

  const authHeader = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/member/lc/pages/summary?range=${range}`,
          { headers: authHeader() },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const seriesAll = {};
        await Promise.all((json.pages || []).map(async (p) => {
          try {
            const s = await fetch(`${BACKEND_URL}/api/member/lc/pages/${p.page_id}/series?range=${range}`, { headers: authHeader() });
            if (s.ok) seriesAll[p.page_id] = (await s.json()).series || [];
          } catch (_) { /* widget tolerates missing */ }
        }));
        if (!cancelled) {
          setSummary(json);
          setSeriesByPage(seriesAll);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [range]);

  const downloadCsv = (pageId) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('admin_token');
    const url = `${BACKEND_URL}/api/member/lc/pages/${pageId}.csv?range=${range}`;
    // Use a temporary fetch + blob to attach auth header (anchor downloads can't carry custom headers)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `lc_analytics_${pageId}_${range}d.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  return (
    <div className="lca" data-testid="member-lc-analytics">
      <div className="lca-header">
        <h1>Lead Capture Analytics</h1>
        <p>Views and leads on your own lead capture pages. Aggregated data only.</p>
      </div>

      <div className="lca-rangebar" data-testid="lca-rangebar">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={r === range ? 'is-active' : ''}
            data-testid={`lca-range-${r}`}
          >
            Last {r} days
          </button>
        ))}
      </div>

      {loading && <div className="lca-empty">Loading…</div>}
      {error && <div className="lca-empty">Failed to load: {error}</div>}

      {summary && !loading && summary.pages?.length === 0 && (
        <div className="lca-empty">
          Publish a lead capture page to start collecting traffic data here.
        </div>
      )}

      {summary && !loading && summary.pages?.length > 0 && (
        <>
          <div className="lca-grid lca-grid--4" style={{ marginBottom: 18 }}>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Pages tracked</span>
              <span className="lca-stat-value">{fmtNum(summary.totals.pages_count)}</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Views (all loads)</span>
              <span className="lca-stat-value" data-testid="member-total-views">{fmtNum(summary.totals.views_total)}</span>
              <span className="lca-stat-sub">{fmtNum(summary.totals.views_unique)} unique people</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Leads</span>
              <span className="lca-stat-value" data-testid="member-total-leads">{fmtNum(summary.totals.conversions)}</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Conversion rate</span>
              <span className="lca-stat-value" style={{ color: 'var(--lca-gold)' }} data-testid="member-avg-cvr">
                {summary.totals.cvr_pct}%
              </span>
            </div></div>
          </div>

          <div className="lca-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="lca-table" data-testid="member-pages-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th className="right">Visits</th>
                  <th className="right">People</th>
                  <th className="right">Leads</th>
                  <th className="right">CVR</th>
                  <th className="right">Last activity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {summary.pages.map((p) => (
                  <React.Fragment key={p.page_id}>
                    <tr data-testid={`member-page-row-${p.page_id}`}>
                      <td>
                        <strong style={{ display: 'block' }}>{p.page_title || p.page_slug || p.page_id}</strong>
                        <div style={{ marginTop: 6 }}><Sparkline series={seriesByPage[p.page_id]} /></div>
                      </td>
                      <td className="right">{fmtNum(p.views_total)}</td>
                      <td className="right">{fmtNum(p.views_unique)}</td>
                      <td className="right">{fmtNum(p.conversions)}</td>
                      <td className="right" style={{ color: 'var(--lca-gold)' }}>{p.cvr_pct}%</td>
                      <td className="right" style={{ color: 'var(--lca-muted)', fontSize: 11 }}>{fmtRelative(p.last_activity_at)}</td>
                      <td className="right" style={{ whiteSpace: 'nowrap' }}>
                        <button className="lca-btn lca-btn--ghost"
                          onClick={() => setExpandedPage(expandedPage === p.page_id ? null : p.page_id)}
                          data-testid={`member-page-details-btn-${p.page_id}`}
                          style={{ marginRight: 6 }}
                        >{expandedPage === p.page_id ? 'Hide' : 'Details'}</button>
                        <button className="lca-btn"
                          onClick={() => downloadCsv(p.page_id)}
                          data-testid={`member-page-csv-btn-${p.page_id}`}
                        >CSV</button>
                      </td>
                    </tr>
                    {expandedPage === p.page_id && (
                      <tr data-testid={`member-page-detail-${p.page_id}`}>
                        <td colSpan={7} style={{ background: 'rgba(245,197,66,0.03)' }}>
                          <div style={{ fontSize: 12, color: 'var(--lca-muted)' }}>
                            Daily breakdown · {range}d
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, marginTop: 8 }}>
                            {(seriesByPage[p.page_id] || []).map((d, i) => {
                              const max = Math.max(1, ...(seriesByPage[p.page_id] || []).map((x) => x.views));
                              return (
                                <div key={i} title={`${d.date}: ${d.views} views, ${d.leads} leads`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, justifyContent: 'flex-end' }}>
                                  <div style={{ height: `${(d.views / max) * 60}px`, background: 'var(--lca-gold-warm)', opacity: 0.8, borderRadius: 2 }} />
                                  <div style={{ height: `${d.leads * 4}px`, background: 'var(--lca-success)', borderRadius: 2 }} />
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--lca-dim)', marginTop: 4, display: 'flex', gap: 12 }}>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--lca-gold-warm)', borderRadius: 2, marginRight: 4 }} />Views</span>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--lca-success)', borderRadius: 2, marginRight: 4 }} />Leads</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lca-privacy">
            <strong>Your data, your stats.</strong> You see aggregated numbers
            only — never raw visitor IPs, never any other member&apos;s pages.
            CSV exports contain daily totals (no visitor-level rows). Visitors
            who send <code>DNT: 1</code> or <code>Sec-GPC: 1</code> are
            respected and not counted. Raw events expire after 180 days.
            Payments on BBC Quick List are non-custodial member-to-member via
            NowPayments / TRON TRC-20 — your analytics page is never linked
            to your wallet.
          </div>
        </>
      )}
    </div>
  );
}
